"use client";

import { useEffect, useState } from "react";
import i18next, { FlatNamespace, KeyPrefix, TFunction } from "i18next";
import {
    FallbackNs,
    UseTranslationOptions,
    initReactI18next,
    useTranslation as useTranslationOrg,
} from "react-i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { getOptions, languages, Language, LocaleAttribute, cookieDefaultLangName, fallbackLng, cookieLangListName } from "./settings";
import { usePathname } from "next/navigation";
import { useCookies } from "react-cookie";

const runsOnServerSide = typeof window === "undefined";

//
i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(resourcesToBackend((language: string, namespace: string) => import(`./locales/${language}/${namespace}.json`)))
    .init({
        ...getOptions(),
        lng: undefined, // let detect the language on client side
        detection: {
            order: ["path", "htmlTag", "cookie", "navigator"],
        },
        preload: runsOnServerSide ? languages : [],
    });

export function useTranslation<Ns extends FlatNamespace, KPrefix extends KeyPrefix<FallbackNs<Ns>> = undefined>(
    ns?: any,
    options?: UseTranslationOptions<KPrefix>,
): { t: TFunction<FallbackNs<Ns>, KPrefix>; lang: Language; loc: LocaleAttribute } {
    const [cookies, setCookie] = useCookies([cookieDefaultLangName, cookieLangListName]);
    const pathName = usePathname();
    const defaultLang = cookies?.[cookieDefaultLangName] ?? fallbackLng;
    const langList = cookies?.[cookieLangListName] ? cookies?.[cookieLangListName].split(",") : languages;    
    const lng = langList.find((l: string) => pathName.startsWith(`/${l}`)) ?? defaultLang;

    const ret = useTranslationOrg(ns, options);
    const { i18n } = ret;
    if (runsOnServerSide && lng && i18n.resolvedLanguage !== lng) {
        i18n.changeLanguage(lng);
    } else {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [activeLng, setActiveLng] = useState(i18n.resolvedLanguage);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (activeLng === i18n.resolvedLanguage) return;
            setActiveLng(i18n.resolvedLanguage);
        }, [activeLng, i18n.resolvedLanguage]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (!lng || i18n.resolvedLanguage === lng) return;
            i18n.changeLanguage(lng);
        }, [lng, i18n]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        // useEffect(() => {
        //     if (cookies.i18next === lng) return;
        //     setCookie(cookieName, lng, { path: "/" });
        // }, [lng, cookies.i18next]);
    }
    return { t: ret.t, lang: lng, loc: lng.toUpperCase() as LocaleAttribute };
}
